import { useState } from 'react';
import type { ICustomControlSupervisionEditProps } from '../../../interfaces/';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { SupervisorsAssessmentBiometrics } from '../../../../../../../common/enums/supervisorsAssessmentBiometrics';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    EnumDropdown,
    Form,
    FormDivider,
    HeaderDivider,
    InputRadioGroup,
    InputTextArea,
} from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../../elements/forms/InputContainer';

export function PilotExaminationV1SupervisionEdit(props: ICustomControlSupervisionEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);

    return (
        <Form onSubmit={async () => null}>
            <HeaderDivider text={translateKey('supervision.qualityProcedure')} />

            <HorizontalFormGroup hasErrors={props.errors.has('assessmentBiometrics')}>
                <FormLabel isRequired>{translateKey('supervision.qualityCheck')}</FormLabel>
                <InputContainer>
                    <EnumDropdown
                        enumName={'supervisorsAssessmentBiometrics'}
                        error={props.errors.get('assessmentBiometrics')}
                        jsEnum={SupervisorsAssessmentBiometrics}
                        onChange={async (value) => await props.handleChange('assessmentBiometrics', value)}
                        onDoValidate={async () => await props.validateOne('assessmentBiometrics')}
                        value={props.supervision.assessmentBiometrics}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup>
                <FormLabel>{translateKey('common.remark')}</FormLabel>
                <InputContainer>
                    <InputTextArea
                        automation="supervisionBiometryRemark"
                        onChange={async (value) => await props.handleChange('remarkBiometrics', value)}
                        placeholder={translateKey('common.remarksOrQuestions')}
                        value={props.supervision.remarkBiometrics}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <FormDivider />
            <HeaderDivider text={translateKey('supervision.qualityReport')} />

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.afwijkend')}>
                <FormLabel>Afwijkend</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        error={props.errors.get('customControlData.afwijkend')}
                        name="afwijkend"
                        onChange={async (value) => await props.handleChangeCustomControl({ afwijkend: value })}
                        onDoValidate={async () => await props.validateOneCustomControl('afwijkend')}
                        options={['Ja', 'Nee', 'Niet te beoordelen']}
                        value={props.supervision.customControlData.afwijkend}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup>
                <FormLabel>{translateKey('common.remark')}</FormLabel>
                <InputContainer>
                    <InputTextArea
                        automation="supervisionGradingRemark"
                        onChange={async (value) => await props.handleChange('remarkGrading', value)}
                        placeholder={translateKey('common.remarksOrQuestions')}
                        value={props.supervision.remarkGrading}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <ButtonContainer
                alignRight
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.supervision}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                    />,
                    <SaveButton
                        buttonDisabled={buttonDisabled}
                        save={async () => {
                            await saveSupervision();
                        }}
                    />,
                    <SaveSendButton
                        buttonDisabled={buttonDisabled}
                        saveSend={async () => {
                            await saveSendSupervision();
                        }}
                    />,
                ]}
            />
        </Form>
    );

    async function saveSendSupervision() {
        setButtonDisabled(true);

        const validatedSupervision = await props.validateSupervision('final');
        if (!validatedSupervision) {
            setButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('final');
        setButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.submitSuccess',
            props.service,
            'supervision',
            translateKey('toast.submitDefaultMessage')
        );
        await props.onSave(supervisionId);
    }

    async function saveSupervision() {
        setButtonDisabled(true);

        const validatedSupervision = await props.validateSupervision('draft');
        if (!validatedSupervision) {
            setButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('draft');
        setButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.draftSuccess',
            props.service,
            'supervision',
            translateKey('toast.draftDefaultMessage')
        );
        await props.onSave(supervisionId);
    }
}

export function pilotExaminationV1SupervisionInitializeCustomControlData() {
    return {
        afwijkend: null,
    };
}
